import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ILegalClient,
  ILegalClientFullObject,
} from '../../Interfaces/ILegalClient';
import { ILegalClientContactInformationType } from '../../Interfaces/ILegalClientContactInformation';
import { ILegalClientLog } from '../../Interfaces/ILegalClientLog';
import { ILegalClientType } from '../../Interfaces/ILegalClientType';
import { customFetcher } from '../useFetch';
import { ILegalClientDocumentType } from '../../Interfaces/ILegalClientDocumentType';
import { ILegalClientDocument } from '../../Interfaces/ILegalClientDocument';
import { IPagination } from '../../Interfaces/IPagination';
import { IRegisterDocumentsResult } from '../../Interfaces/IKi';

//////////////////// LegalClients
export const useGetLegalClients = (searchUri?: string) => {
  const abortController = new AbortController();

  const {
    data: legalClients,
    isLoading: isInitialLoadingLegalClients,
    isRefetching: isRefetchingLegalClients,
    refetch: refetchLegalClients,
  } = useQuery<IPagination<ILegalClient[]>>({
    refetchOnWindowFocus: true,
    queryKey: ['legalClients', 'search', searchUri],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalclient?${searchUri}`,
        signal: abortController.signal,
      }),
    enabled: !!searchUri,
  });

  const abort = () => {
    abortController.abort();
  };

  return {
    legalClients,
    isLoadingLegalClients: isInitialLoadingLegalClients,
    isRefetchingLegalClients,
    refetchLegalClients,
    abortLegalClients: abort,
  };
};

export const useGetLegalClientFullObject = (idLegalClient: number) => {
  const {
    data: legalClientFullObject,
    isFetching: isFetchingLegalClientFullObject,
    refetch: refetchLegalClientFullObject,
  } = useQuery<ILegalClientFullObject>({
    queryKey: ['legalclient/fullObject', idLegalClient],
    queryFn: () =>
      customFetcher({ endpoint: `/legalclient/fullObject/${idLegalClient}` }),
  });

  return {
    legalClientFullObject,
    isFetchingLegalClientFullObject,
    refetchLegalClientFullObject,
  };
};

export const useCreateLegalClientFullObject = () => {
  const {
    mutateAsync: createLegalClientFullObject,
    isPending: isPendingCreateLegalClientFullObject,
  } = useMutation({
    mutationFn: (legalClient: ILegalClientFullObject) =>
      customFetcher({
        endpoint: '/legalclient/fullObject',
        method: 'POST',
        body: legalClient,
      }),
  });

  return {
    createLegalClientFullObject,
    isPendingCreateLegalClientFullObject,
  };
};

export const useUpdateLegalClientFullObject = () => {
  const {
    mutateAsync: updateLegalClientFullObject,
    isPending: isPendingUpdateLegalClientFullObject,
  } = useMutation({
    mutationFn: (legalClient: ILegalClientFullObject) =>
      customFetcher({
        endpoint: '/legalclient/fullObject',
        method: 'PUT',
        body: legalClient,
      }),
  });
  return {
    updateLegalClientFullObject,
    isPendingUpdateLegalClientFullObject,
  };
};

export const useGetLegalClientTypes = () => {
  const {
    data: legalClientTypes,
    isLoading: isLoadingLegalClientTypes,
    refetch: refetchLegalClientTypes,
  } = useQuery<ILegalClientType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclienttype'],
    queryFn: () => customFetcher({ endpoint: '/legalclienttype' }),
  });

  return {
    legalClientTypes,
    isLoadingLegalClientTypes,
    refetchLegalClientTypes,
  };
};

export const useGetLegalClientContactInformationTypes = () => {
  const {
    data: contactInformationTypes,
    isLoading: isLoadingContactInformationTypes,
    refetch: refetchContactInformationTypes,
  } = useQuery<ILegalClientContactInformationType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['contactinformationtype'],
    queryFn: () => customFetcher({ endpoint: '/contactinformationtype' }),
  });

  return {
    contactInformationTypes,
    isLoadingContactInformationTypes,
    refetchContactInformationTypes,
  };
};

export const useDeleteLegalClient = () => {
  const {
    mutateAsync: deleteLegalClient,
    isPending: isPendingDeleteLegalClient,
  } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/legalclient/${id}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteLegalClient,
    isPendingDeleteLegalClient,
  };
};

export const useGetLegalClientLogs = () => {
  const {
    data: legalClientLogs,
    isLoading: isLoadingLegalClientLogs,
    refetch: refetchLegalClientLogs,
  } = useQuery<ILegalClientLog[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclientlog'],
    queryFn: () => customFetcher({ endpoint: '/legalclientlog' }),
  });

  return {
    legalClientLogs,
    isLoadingLegalClientLogs,
    refetchLegalClientLogs,
  };
};

export const useGetLegalClientLogsByLegalClient = (idLegalClient: number) => {
  const {
    data: legalClientLogs,
    isLoading: isLoadingLegalClientLogs,
    refetch: refetchLegalClientLogs,
  } = useQuery<ILegalClientLog[]>({
    queryKey: ['legalclient/log', idLegalClient],
    queryFn: () =>
      customFetcher({ endpoint: `/legalclient/log/${idLegalClient}` }),
  });

  return {
    legalClientLogs,
    isLoadingLegalClientLogs,
    refetchLegalClientLogs,
  };
};

////////////////////
/// GET Documents for LegalClient
export const useGetLegalClientDocuments = (idLegalClient: number) => {
  const {
    data: legalClientDocuments,
    isLoading: isLoadingLegalClientDocuments,
    refetch: refetchLegalClientDocuments,
  } = useQuery<ILegalClientDocument[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclientdocument', idLegalClient],
    queryFn: () =>
      customFetcher({
        endpoint: `/legalclientdocument?idLegalClient=${idLegalClient}`,
      }),
  });

  return {
    legalClientDocuments,
    isLoadingLegalClientDocuments,
    refetchLegalClientDocuments,
  };
};

////////////////////
/// GET Document Types
export const useGetLegalClientDocumentTypes = () => {
  const {
    data: legalClientDocumentTypes,
    isLoading: isLoadingLegalClientDocumentTypes,
    refetch: refetchLegalClientDocumentTypes,
  } = useQuery<ILegalClientDocumentType[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclientdocumenttype'],
    queryFn: () => customFetcher({ endpoint: '/legalclientdocumenttype' }),
  });

  return {
    legalClientDocumentTypes,
    isLoadingLegalClientDocumentTypes,
    refetchLegalClientDocumentTypes,
  };
};

////////////////////
/// UPLOAD Document
export const useUploadDocument = () => {
  const { mutateAsync: uploadDocument, isPending: isPendingUploadDocument } =
    useMutation({
      mutationFn: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        return customFetcher({
          endpoint: '/document/upload',
          method: 'POST',
          body: formData,
        });
      },
    });

  return {
    uploadDocument,
    isPendingUploadDocument,
  };
};

////////////////////
/// CREATE Document Relation
export const useCreateLegalClientDocument = () => {
  const {
    mutateAsync: createLegalClientDocument,
    isPending: isPendingCreateLegalClientDocument,
  } = useMutation({
    mutationFn: (input: {
      idLegalClient: number;
      idDocument: number;
      idLegalClientDocumentType: number;
      idRelationship?: number | null;
      idLegalTransaction?: number | null;
    }) =>
      customFetcher({
        endpoint: '/legalclientdocument',
        method: 'POST',
        body: input,
      }),
  });

  return {
    createLegalClientDocument,
    isPendingCreateLegalClientDocument,
  };
};

////////////////////
/// DELETE Document Relation
export const useDeleteLegalClientDocument = () => {
  const {
    mutateAsync: deleteLegalClientDocument,
    isPending: isPendingDeleteLegalClientDocument,
  } = useMutation({
    mutationFn: (input: { idLegalClient: number; idDocument: number }) =>
      customFetcher({
        endpoint: `/legalclientdocument`,
        method: 'DELETE',
        body: input,
      }),
  });

  return {
    deleteLegalClientDocument,
    isPendingDeleteLegalClientDocument,
  };
};

export const useDownloadRegisterDocuments = () => {
  return useMutation({
    mutationFn: async (params: {
      idLegalClient: number;
      types: string[];
      /** Legt die Dokumente zusätzlich zum Vorgang ab. */
      idLegalTransaction?: number;
      /** Eine bestimmte Gesellschafterliste (YYYY-MM-DD) statt der neuesten. */
      glDatum?: string;
    }): Promise<IRegisterDocumentsResult> => {
      return customFetcher({
        endpoint: `/legalclient/${params.idLegalClient}/download-register-documents`,
        method: 'POST',
        body: {
          types: params.types,
          ...(params.idLegalTransaction !== undefined && {
            idLegalTransaction: params.idLegalTransaction,
          }),
          ...(params.glDatum !== undefined && { glDatum: params.glDatum }),
        },
      });
    },
  });
};

export const useCheckEmail = () => {
  const abortController = new AbortController();

  const { mutateAsync: checkEmail, isPending: isPendingCheckEmail } =
    useMutation<ILegalClient[], Error, { Email: string }>({
      mutationFn: searchObject =>
        customFetcher({
          endpoint: '/legalclient/checkEmail',
          method: 'POST',
          body: searchObject,
          signal: abortController.signal,
        }),
    });
  const abort = () => {
    if (isPendingCheckEmail) {
      abortController.abort();
    }
  };

  return {
    checkEmail,
    isPendingCheckEmail,
    abortCheckMail: abort,
  };
};
